import { memo, Suspense, useCallback } from "react"
import { AuthProvider } from "@/auth"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { BrowserRouter, useRoutes } from "react-router-dom"

import { ErrorBoundary, RouteTracker } from "@be-miniapp/logging"
import { AnimationRouterContextProvider } from "@be-miniapp/ui/components/animated-context"
import { MapProvider } from "@be-miniapp/ui/components/map/map-context"
import { NativePageTransition } from "@be-miniapp/ui/components/native-page-transition"
import { NavigateHomeButton } from "@be-miniapp/ui/components/navigate-home-button"
import { SharedNavigateContextProvider } from "@be-miniapp/ui/components/shared-navigate-context"
import { TailwindIndicator } from "@be-miniapp/ui/components/tailwind-indicator"
import { Button } from "@be-miniapp/ui/components/ui/button"
import { SpinnerScreen } from "@be-miniapp/ui/components/ui/spinner"
import { Toaster } from "@be-miniapp/ui/components/ui/toaster"

import { ModalOverlay } from "@/components/modal"
import { PermissionWall } from "@/components/permission-wall"
import { TouchInitialization } from "@/components/session-wrapper"

import { routes } from "./routes.config"

import "@/utils/axios"

// Memoize Fallback component to prevent unnecessary re-renders
const Fallback = memo(function Fallback({ resetError }: any) {
  const handleReset = useCallback(() => {
    resetError()
  }, [resetError])

  return (
    // eslint-disable-next-line tailwindcss/enforces-shorthand
    <div className="fixed inset-0 h-screen w-screen overflow-hidden overflow-y-auto">
      <div className="inset-0 flex flex-col items-center justify-center p-5">
        <div className="mt-10 flex w-full flex-col gap-5 overflow-auto px-5">
          <img src="/icons/be.svg" className="mx-auto size-10" />
          <span className="text-center font-medium text-gray-900 text-lg">
            Xin lỗi vì sự bất tiện này
          </span>{" "}
          <span className="text-center text-gray-600 text-sm">
            Có lỗi đã xảy ra trong quá trình xử lý yêu cầu của bạn. Vui lòng thử
            lại hoặc
            <a href="tel:1900232345" className="text-blue-600">
              {" "}
              liên hệ
            </a>{" "}
            với chúng tôi để được hỗ trợ. Lỗi này đã được ghi lại và sẽ được xử
            lý sớm nhất có thể.
          </span>
          <div className="flex flex-col gap-3">
            <Button
              onClick={handleReset}
              variant="secondary"
              className="h-8 bg-gray-200"
            >
              Thử lại
            </Button>

            <NavigateHomeButton />

            <span className="text-gray-600 text-xs">
              Note: trong trường hợp thử lại không hoạt động, xin vui lòng mở
              lại be từ ứng dụng đang chạy.
            </span>
          </div>
        </div>
      </div>
    </div>
  )
})

// Move QueryClient outside component to prevent recreation on every render
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    },
  },
})

const AnimatedRoutes = memo(() => {
  const routeElements = useRoutes(routes)

  return <NativePageTransition>{routeElements}</NativePageTransition>
})

const App = () => {
  return (
    <ErrorBoundary fallback={Fallback}>
      <QueryClientProvider client={queryClient}>
        <Suspense fallback={<SpinnerScreen />}>
          <AnimationRouterContextProvider>
            <BrowserRouter>
              <RouteTracker />
              <AuthProvider>
                <SharedNavigateContextProvider>
                  <MapProvider>
                    <TouchInitialization />
                    <TailwindIndicator />
                    <AnimatedRoutes />
                    <PermissionWall />
                  </MapProvider>
                </SharedNavigateContextProvider>
              </AuthProvider>
            </BrowserRouter>
          </AnimationRouterContextProvider>
        </Suspense>
      </QueryClientProvider>
      <Toaster />
      <ModalOverlay />
    </ErrorBoundary>
  )
}

export { App }
